import { useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import * as uuid from 'uuid';
import { useQuery } from '@apollo/client';
import { useInstallationContext } from '../../../../../context/installation';
import {
  ActionDetailsQuery,
  ActionDetailsQueryVariables,
  ActivityType,
  BlindStateDirectionInternal,
  ChannelTypeInternal,
  GateEventTypeInternal,
} from '../../../../../data-access/gql-types/graphql';
import { ACTION_DETAILS } from '../../../../../data-access/queries/actions';
import {
  BlindStateTask,
  DimStateTask,
  GatePartialControlModeStateTask,
  GateDailyModeStateTask,
  GateStateTask,
  OnOffStateTask,
  RGBWStateTask,
} from '../../../../../types/actions';
import { useDevicesAndChannelsContext } from '../../../context/devices-and-channels';
import { useLavvaCreateActionContext } from '../context';
import { DeviceTaskParams, GateActionInternal, SceneTaskType, Task, TaskVariant } from '../types';

export const useActionEdit = () => {
  const location = useLocation();
  const { setName, setIcon, setTaskList, clearAll, setIsDuplicate } = useLavvaCreateActionContext();
  const { selectedInstallationId } = useInstallationContext();
  const { channelList } = useDevicesAndChannelsContext();
  const { actionId } = useParams<{ actionId: string }>();
  const id = actionId || location.state?.actionId;
  const { data } = useQuery<ActionDetailsQuery, ActionDetailsQueryVariables>(ACTION_DETAILS, {
    variables: { actionId: id, installationId: selectedInstallationId },
    skip: !id,
  });

  useEffect(() => {
    clearAll();
  }, []);

  const getVariantByActivityType = (type: ActivityType, channelType: ChannelTypeInternal) => {
    switch (type) {
      case ActivityType.On:
      case ActivityType.Off:
        return TaskVariant.OnOff;
      case ActivityType.Open:
      case ActivityType.Close:
      case ActivityType.Stop:
      case ActivityType.SetPosition:
        return channelType === ChannelTypeInternal.Gate ? TaskVariant.SetGate : TaskVariant.SetBlind;
      case ActivityType.SetDim:
        return TaskVariant.Dim;
      case ActivityType.SetLightColor:
        return TaskVariant.SetRGBW;
      case ActivityType.SetDailyModeOn:
      case ActivityType.SetDailyModeOff:
        return TaskVariant.SetGateDailyMode;
      case ActivityType.SetPartialControlModeOn:
      case ActivityType.SetPartialControlModeOff:
        return TaskVariant.SetGatePartialControlMode;
      default:
        return TaskVariant.Undefined;
    }
  };

  const getStateByVariant = (variant: TaskVariant, activityType: ActivityType, value: string) => {
    switch (variant) {
      case TaskVariant.OnOff:
        return { isOn: activityType === ActivityType.On } as OnOffStateTask;
      case TaskVariant.Dim:
        return { brightness: parseInt(value) } as DimStateTask;
      case TaskVariant.SetRGBW: {
        const [R, G, B] = value.split(',').map((x) => parseInt(x));
        return { R, G, B } as RGBWStateTask;
      }
      case TaskVariant.SetBlind: {
        if (activityType === ActivityType.Open) {
          return {
            action: BlindStateDirectionInternal.Open,
            position: null,
          } as BlindStateTask<BlindStateDirectionInternal>;
        }

        if (activityType === ActivityType.Close) {
          return {
            action: BlindStateDirectionInternal.Closed,
            position: null,
          } as BlindStateTask<BlindStateDirectionInternal>;
        }

        if (activityType === ActivityType.Stop) {
          return {
            action: BlindStateDirectionInternal.Stopped,
            position: null,
          } as BlindStateTask<BlindStateDirectionInternal>;
        }

        return {
          action: BlindStateDirectionInternal.Unknown,
          position: value ? parseInt(value) : 0,
        } as BlindStateTask<BlindStateDirectionInternal>;
      }
      case TaskVariant.SetGate: {
        if (activityType === ActivityType.Open) {
          return {
            action: GateActionInternal.Open,
            position: null,
          } as GateStateTask<GateActionInternal>;
        }

        if (activityType === ActivityType.Close) {
          return {
            action: GateActionInternal.Closed,
            position: null,
          } as GateStateTask<GateActionInternal>;
        }

        if (activityType === ActivityType.Stop) {
          return {
            action: GateActionInternal.Stopped,
            position: null,
          } as GateStateTask<GateActionInternal>;
        }

        return {
          action: GateActionInternal.Unknown,
          position: value ? parseInt(value) : 0,
        } as GateStateTask<GateActionInternal>;
      }
      case TaskVariant.SetGateDailyMode: {
        if (activityType === ActivityType.SetDailyModeOn) {
          return {
            action: GateEventTypeInternal.SetDailyModeOn,
          } as GateDailyModeStateTask<GateEventTypeInternal>;
        }

        if (activityType === ActivityType.SetDailyModeOff) {
          return {
            action: GateEventTypeInternal.SetDailyModeOff,
          } as GateDailyModeStateTask<GateEventTypeInternal>;
        }

        return {
          action: GateEventTypeInternal.Unknown,
        } as GateDailyModeStateTask<GateEventTypeInternal>;
      }
      case TaskVariant.SetGatePartialControlMode: {
        if (activityType === ActivityType.SetPartialControlModeOn) {
          return {
            action: GateEventTypeInternal.SetPartialControlModeOn,
          } as GatePartialControlModeStateTask<GateEventTypeInternal>;
        }

        if (activityType === ActivityType.SetPartialControlModeOff) {
          return {
            action: GateEventTypeInternal.SetPartialControlModeOff,
          } as GatePartialControlModeStateTask<GateEventTypeInternal>;
        }

        return {
          action: GateEventTypeInternal.Unknown,
        } as GatePartialControlModeStateTask<GateEventTypeInternal>;
      }
      default:
        return null;
    }
  };

  useEffect(() => {
    if (data?.userAction) {
      setName(data.userAction.action.name);
      setIcon(data.userAction.iconName);

      if (location.state?.actionId) setIsDuplicate(true);

      const tasks = data.userAction.action.channelActions.map((action) => {
        const channel = channelList.find((c) => c.id === action.channelId);
        const variant = getVariantByActivityType(action.activityType, action.channelType);
        const state = getStateByVariant(variant, action.activityType, action.value);

        if (channel && state) {
          return {
            id: uuid.v4(),
            taskType: SceneTaskType.ChannelState,
            taskParams: {
              channel,
              state,
              variant,
            } as DeviceTaskParams,
          };
        }
      }) as Task[];

      setTaskList(tasks);
    }
  }, [data, channelList, location.state?.actionId]);
};
